import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, PencilSquareIcon, CheckIcon } from '@heroicons/react/24/outline';

const EditVehicleModal = ({ vehicle, onClose, onSave }) => {
  const [form, setForm] = useState({
    id: vehicle.id,
    vehicleMake: vehicle.vehicleMake || '',
    vehicleModel: vehicle.vehicleModel || '',
    year: vehicle.year || '',
    VIN: vehicle.VIN || '',
    registrationNumber: vehicle.registrationNumber || '',
    engineType: vehicle.engineType || '',
  });

  useEffect(() => {
    setForm({
      id: vehicle.id,
      vehicleMake: vehicle.vehicleMake || '',
      vehicleModel: vehicle.vehicleModel || '',
      year: vehicle.year || '',
      VIN: vehicle.VIN || '',
      registrationNumber: vehicle.registrationNumber || '',
      engineType: vehicle.engineType || '',
    });
  }, [vehicle]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl">
                        <PencilSquareIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-left">
                        <Dialog.Title className="text-xl font-black text-white">
                          Edit Vehicle Details
                        </Dialog.Title>
                        <p className="text-sm text-purple-100 mt-1">
                          Update vehicle information for {vehicle.vehicleMake} {vehicle.vehicleModel}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg hover:bg-white/20 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={submit} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Vehicle Make */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Vehicle Make
                      </label>
                      <input
                        name="vehicleMake"
                        value={form.vehicleMake}
                        onChange={onChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                        placeholder="e.g., Toyota, Honda"
                      />
                    </div>

                    {/* Vehicle Model */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Vehicle Model
                      </label>
                      <input
                        name="vehicleModel"
                        value={form.vehicleModel}
                        onChange={onChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                        placeholder="e.g., Corolla, Civic"
                      />
                    </div>

                    {/* Year */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Year
                      </label>
                      <input
                        name="year"
                        value={form.year}
                        onChange={onChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                        placeholder="e.g., 2024"
                      />
                    </div>

                    {/* VIN */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        VIN
                      </label>
                      <input
                        name="VIN"
                        value={form.VIN}
                        onChange={onChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                        placeholder="Vehicle Identification Number"
                      />
                    </div>

                    {/* Registration Number */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Registration Number
                      </label>
                      <input
                        name="registrationNumber"
                        value={form.registrationNumber}
                        onChange={onChange}
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                        placeholder="e.g., ABC-123"
                      />
                    </div>

                    {/* Engine Type */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Engine Type
                      </label>
                      <input
                        name="engineType"
                        value={form.engineType}
                        onChange={onChange}
                        placeholder="e.g., Petrol, Diesel, Hybrid"
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2.5 transition-all duration-200"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <CheckIcon className="h-5 w-5" />
                      Save Changes
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditVehicleModal;
